import {
  ArcElement,
  Chart as ChartJS,
  ChartOptions,
  Title as ChartTitlePlugin,
  Legend,
  Plugin,
  Tooltip
} from "chart.js";
import React, { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { OrdersTotalsWithPercent } from "../../common/interfaces";
import api from "../../services/api";
import { ChartContainer, ChartTitle } from "./dashboard.styled";

ChartJS.register(ArcElement, Tooltip, Legend, ChartTitlePlugin);

// Plugin para escrever o TOTAL no centro do doughnut
const centerTextPlugin: Plugin<"doughnut"> = {
  id: "centerText",
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;
    const meta = chart.config.data as any;
    const totalAll: number | undefined = meta?.datasets?.[0]?.totalAll;
    if (!totalAll) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    const rootStyles = getComputedStyle(document.documentElement);
    const textColor = rootStyles.getPropertyValue("--text").trim() || "#0a1128";
    const mutedColor = rootStyles.getPropertyValue("--text-muted").trim() || "#666";

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "600 14px sans-serif";
    ctx.fillStyle = textColor;
    ctx.fillText(`${totalAll}`, centerX, centerY);
    ctx.font = "400 11px sans-serif";
    ctx.fillStyle = mutedColor;
    ctx.fillText("total", centerX, centerY + 16);
    ctx.restore();
  }
};

type Props = {
  /** Altura do gráfico em px (default: 260) */
  height?: number;
  /** Largura máxima do gráfico (px ou %). Ex.: 480, "520px", "100%" (default: 520) */
  maxWidth?: number | string;
  /** Endpoint do resumo (default: /dashboard/orders-summary) */
  endpoint?: string;
  /** Título mostrado acima do gráfico */
  title?: string;
};

const OrdersSummaryDoughnut: React.FC<Props> = ({
  height = 260,
  maxWidth = 520,
  endpoint = "/dashboard/orders-summary",
  title = "Resumo de Ordens (últimos 30 dias)"
}) => {
  const [payload, setPayload] = useState<OrdersTotalsWithPercent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get<OrdersTotalsWithPercent>(endpoint);
        if (mounted) setPayload(data);
      } catch (e) {
        console.error("Erro ao buscar resumo de ordens:", e);
        if (mounted) {
          setPayload({
            totalInProcess: 0,
            totalCompleted: 0,
            totalCanceled: 0,
            totalAll: 0,
            percentages: { inProcess: 0, completed: 0, canceled: 0 },
            pie: [
              { label: "Em processo", value: 0, count: 0 },
              { label: "Concluídas", value: 0, count: 0 },
              { label: "Canceladas", value: 0, count: 0 }
            ],
            window: { from: "", to: "", field: "created_at" }
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [endpoint]);

  const chartData = useMemo(() => {
    const labels = payload?.pie.map((p) => p.label) ?? [
      "Em processo",
      "Concluídas",
      "Canceladas"
    ];
    const percents = payload?.pie.map((p) => p.value) ?? [0, 0, 0];
    const counts = payload?.pie.map((p) => p.count) ?? [0, 0, 0];

    return {
      labels,
      datasets: [
        {
          label: "Ordens (%)",
          data: percents, // percentuais (0..100)
          counts, // totais absolutos para tooltip
          totalAll: payload?.totalAll ?? 0,
          backgroundColor: [
            "rgba(54, 162, 235, 0.75)", // Em processo
            "rgba(75, 192, 192, 0.75)", // Concluídas
            "rgba(255, 99, 132, 0.75)" // Canceladas
          ],
          borderColor: [
            "rgba(54, 162, 235, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(255, 99, 132, 1)"
          ],
          borderWidth: 1,
          hoverOffset: 4
        } as any
      ]
    };
  }, [payload]);

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false, // **importante** para respeitar a altura do container
    cutout: "65%", // anel (pode reduzir/aumentar)
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 16 }
      },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || "";
            const value = ctx.parsed as number; // percentual
            const countsArr = (ctx.dataset as any).counts as number[];
            const count = countsArr?.[ctx.dataIndex] ?? 0;
            const plural = count === 1 ? "ordem" : "ordens";
            return `${label}: ${value}% (${count} ${plural})`;
          }
        }
      }
    }
  };

  return (
    <ChartContainer>
      <ChartTitle>{title}</ChartTitle>

      {loading ? (
        <div
          style={{
            minHeight: height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          Carregando…
        </div>
      ) : !payload || payload.totalAll === 0 ? (
        <div
          style={{
            width: "100%",
            minHeight: height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ccc",
            borderRadius: 8,
            color: "#666",
            fontSize: 14,
            padding: 16
          }}
        >
          Sem dados nos últimos 30 dias.
        </div>
      ) : (
        // Container controla o tamanho final do gráfico
        <div
          style={{
            position: "relative",
            height, // altura em px
            maxWidth, // largura máxima
            margin: "0 auto" // centraliza
          }}
        >
          <Doughnut
            data={chartData}
            options={options}
            plugins={[centerTextPlugin]}
          />
        </div>
      )}
    </ChartContainer>
  );
};

export default OrdersSummaryDoughnut;
